import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingOverlay,
    FloatingPortal,
    offset,
    Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useMergeRefs,
    useRole,
} from '@floating-ui/react'
import { AnimatePresence, motion } from 'motion/react'
import { cloneElement, isValidElement, MouseEventHandler, ReactNode, Ref, RefObject, useLayoutEffect, useState } from 'react'

import { astraTwMerge } from '../Utilities/astraTwMerge'
import { FLOATING_MOTION_PROPS, FLOATING_OVERLAY_MOTION_PROPS, TRANSFORM_ORIGIN_BY_PLACEMENT } from '../Utilities/floatingMotion'

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right'
export type PopoverAlign = 'start' | 'center' | 'end'

export type NativePopoverProps = {
    /** The element to trigger the popover. React components must spread props and pass the ref prop to their underlying element. */
    children?: ReactNode
    /** The content to display inside the popover. */
    popoverContent: ReactNode
    /** Callback function that gets called when the popover open state changes. */
    onOpenChange?: (open: boolean) => void
    /** Remove padding from the popover content. */
    noPadding?: boolean
    /** If true, the popover will be modal. */
    isModal?: boolean
    /** Control the open state of the popover. */
    isOpen?: boolean
    /** A data-test attribute on the trigger element. */
    'data-test'?: string
    /**
     * Accessible name for the panel. The panel is a `role="dialog"`, so without a name a screen reader
     * announces it as an unnamed dialog and the user has to explore it to learn what opened. Name it after
     * the trigger: a "Filters" button opens a "Filters" panel.
     */
    'aria-label'?: string
    /**
     * Id of the element inside the panel that already names it, such as a heading. Takes precedence over
     * `aria-label`, so pass one or the other.
     */
    'aria-labelledby'?: string
    /** Show an overlay behind the popover content. */
    showOverlay?: boolean
    /** Disable the popover trigger. */
    disabled?: boolean
    /** Disable animations. */
    reducedMotion?: boolean
    /** Additional className for the popover content container. */
    className?: string
    /** The preferred side of the trigger to render the popover. */
    side?: PopoverSide
    /** The preferred placement against the trigger. */
    align?: PopoverAlign
    /** The distance in pixels from the trigger. */
    sideOffset?: number
    /**
     * Specify a container element to portal the content into.
     */
    container?: HTMLElement | null
    /**
     * Optional ref to an element that the popover will be anchored to.
     */
    anchorRef?: RefObject<HTMLDivElement | null>
    /** If true, the popover will allow click and keydown events to propagate to parent elements. */
    allowEventPropagation?: boolean
    /** If true, the popover will match the width of the trigger element. */
    matchTriggerWidth?: boolean
}

const DEFAULT_SIDE_OFFSET = 5

/** FloatingOverlay renders a plain div, so it needs wrapping to take part in the enter and exit animations. */
const MotionFloatingOverlay = motion.create(FloatingOverlay)

type TriggerElementProps = {
    ref?: Ref<Element>
    onClick?: MouseEventHandler<Element>
    [key: string]: unknown
}

/**
 * Convert side and align to FloatingUI placement
 */
function getPlacement(side: PopoverSide, align: PopoverAlign): Placement {
    if (align === 'center') return side as Placement
    return `${side}-${align}` as Placement
}

export const AstraPopover = ({
    children,
    popoverContent,
    onOpenChange,
    noPadding = false,
    showOverlay = false,
    isOpen,
    'data-test': dataTest,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    disabled,
    isModal = true,
    reducedMotion = false,
    className,
    side = 'bottom',
    align = 'center',
    sideOffset = DEFAULT_SIDE_OFFSET,
    matchTriggerWidth = false,
    container,
    anchorRef,
    allowEventPropagation = true,
}: NativePopoverProps) => {
    const isControlled = isOpen !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const currentOpen = isControlled ? isOpen : uncontrolledOpen

    const setOpen = (next: boolean) => {
        if (!isControlled) setUncontrolledOpen(next)
        onOpenChange?.(next)
    }

    const { x, y, strategy, refs, context, elements, placement } = useFloating({
        open: currentOpen,
        onOpenChange: setOpen,
        placement: getPlacement(side, align),
        whileElementsMounted: autoUpdate,
        middleware: [offset({ mainAxis: sideOffset }), flip(), shift({ padding: { right: 28, left: 28 } })],
    })

    const click = useClick(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'dialog' })
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

    const triggerRef = isValidElement<TriggerElementProps>(children) ? children.props.ref : undefined
    const setTriggerRef = useMergeRefs<Element>([anchorRef ? undefined : refs.setReference, triggerRef])

    useLayoutEffect(() => {
        if (!anchorRef) return
        refs.setReference(anchorRef.current)
    }, [anchorRef, refs])

    if (disabled) return <>{children}</>
    if (!anchorRef && !isValidElement<TriggerElementProps>(children)) return <>{children}</>

    const referenceProps = isValidElement<TriggerElementProps>(children)
        ? getReferenceProps({
              ...children.props,
              onClick: event => {
                  children.props.onClick?.(event)
                  if (!allowEventPropagation) event.stopPropagation()
              },
          })
        : undefined

    return (
        <>
            {isValidElement<TriggerElementProps>(children)
                ? cloneElement(children, {
                      ...referenceProps,
                      'data-test': dataTest ?? children.props['data-test'],
                      ref: setTriggerRef,
                  })
                : children}

            <AnimatePresence>
                {currentOpen && (
                    <FloatingPortal root={container}>
                        {showOverlay && <MotionFloatingOverlay {...(reducedMotion ? {} : FLOATING_OVERLAY_MOTION_PROPS)} className="z-elevated bg-black/25" />}
                        <FloatingFocusManager context={context} modal={isModal}>
                            <motion.div
                                ref={refs.setFloating}
                                tabIndex={-1}
                                {...(reducedMotion ? {} : FLOATING_MOTION_PROPS)}
                                style={{
                                    position: strategy,
                                    top: y,
                                    left: x,
                                    width: matchTriggerWidth ? elements.reference?.getBoundingClientRect().width : undefined,
                                }}
                                className={astraTwMerge(
                                    'z-elevated rounded-xl border border-base bg-surface-base shadow-popover outline-none',
                                    TRANSFORM_ORIGIN_BY_PLACEMENT[placement],
                                    noPadding ? '' : 'p-3',
                                    className
                                )}
                                aria-label={ariaLabel}
                                aria-labelledby={ariaLabelledBy}
                                {...getFloatingProps(
                                    allowEventPropagation
                                        ? {}
                                        : {
                                              onClick: event => {
                                                  event.stopPropagation()
                                              },
                                              onKeyDown: event => {
                                                  event.stopPropagation()
                                              },
                                          }
                                )}
                            >
                                {popoverContent}
                            </motion.div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </AnimatePresence>
        </>
    )
}
